import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { offerIssuerService } from '../services/offerIssuerService';    
import { OfferIssuerState } from '../models/state/offerIssuerState';
import { OfferIssuerResource } from '../models/resource/offerIssuerResource';
import { OfferIssuerRequest } from '../models/request/offerIssuerRequest';
import { OfferIssuerModel } from '../models/models/offerIssuerModel';
import { OfferIssuerResponse } from '../models/response/offerIssuerResponse';

const initialState: OfferIssuerState = {
    offerIssuers: {
        token: null,
        status: 'idle',
        isLoading: false,
        error: null,
        message: '',
        blobData: null,
        data:null,
    }, 
    offerIssuerDetails: {
        token: null,
        status: 'idle',
        isLoading: false,
        error: null,
        message: '',
        blobData: null,
        data:null,
    }, 
    offerIssuerList: {
        token: null,
        status: 'idle',
        isLoading: false,
        error: null,
        message: '',
        blobData: null,
        data:null,
    }, 
};
 

export const getOfferIssuer = createAsyncThunk<
    any, 
    void,
  { rejectValue: string }
  >('offerIssuer/getOfferIssuer', async (_, { rejectWithValue }) => {
  try {
    const response = await offerIssuerService.getOfferIssuer();
    return response;
  } catch (error: any) {
    return rejectWithValue('An unexpected error occurred');
  }
});  

export const getOfferIssuerList = createAsyncThunk<
OfferIssuerResponse,
  { data: OfferIssuerRequest },
  { rejectValue: string }
>(
  "offerIssuer/getOfferIssuerList",
  async ({ data }, { rejectWithValue }) => {
    try {
      const response =
        await offerIssuerService.getOfferIssuerList(data);
      return response;
    } catch (error: any) {
      return rejectWithValue("An unexpected error occurred");
    }
  }
); 

export const getOfferIssuerById = createAsyncThunk<
  OfferIssuerModel,
  { id: number },
  { rejectValue: string }
>(
  "offerIssuer/getOfferIssuerById",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response =
        await offerIssuerService.getOfferIssuerById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue("An unexpected error occurred");
    }
  }
); 

export const deleteOfferIssuer = createAsyncThunk<
OfferIssuerResponse,
  { data: number },
  { rejectValue: string }
>(
  "offerIssuer/deleteOfferIssuer",
  async ({ data }, { rejectWithValue }) => {
    try {
      const response =
        await offerIssuerService.deleteOfferIssuer(data);
      return response;
    } catch (error: any) {
      return rejectWithValue("An unexpected error occurred");
    }
  }
);  

export const saveOfferIssuer = createAsyncThunk<
OfferIssuerModel,
  { data: OfferIssuerResource },
  { rejectValue: string }
>(
  "offerIssuer/saveOfferIssuer",
  async ({ data }, { rejectWithValue }) => {
    try {
      const response = await offerIssuerService.saveOfferIssuer(data);
      return response;
    } catch (error: any) {
      return rejectWithValue("An unexpected error occurred");
    }
  }
); 


const offerIssuerReducer = createSlice({
  name: 'lookup',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getOfferIssuer.pending, (state) => {
        state.offerIssuerDetails.status = 'loading';
        state.offerIssuerDetails.isLoading = true;
      })
      .addCase(getOfferIssuer.fulfilled, (state, action) => {
        state.offerIssuerDetails.status = 'idle';
        state.offerIssuerDetails.data = action.payload;
        state.offerIssuerDetails.isLoading = false; 
      })
      .addCase(getOfferIssuer.rejected, (state, action) => {
        state.offerIssuerDetails.status = 'failed';
        state.offerIssuerDetails.isLoading= false;
        if (action.payload) {
          state.offerIssuerDetails.error = action.payload;
          state.offerIssuerDetails.message = action.payload;
        } else {
          state.offerIssuerDetails.message = 'An unexpected error occurred';
        }
      }) 
      .addCase(getOfferIssuerList.pending, (state) => {
        state.offerIssuerList.status = "loading";
        state.offerIssuerList.isLoading = true;
      })
      .addCase(getOfferIssuerList.fulfilled, (state, action) => {
        state.offerIssuerList.status = "idle";
        state.offerIssuerList.data = action.payload;
        state.offerIssuerList.isLoading = false;
      })
      .addCase(getOfferIssuerList.rejected, (state, action) => {
        state.offerIssuerList.status = "failed";
        state.offerIssuerList.isLoading = false;
        if (action.payload) {
          state.offerIssuerList.error = action.payload;
          state.offerIssuerList.message = action.payload;
        } else {
          state.offerIssuerList.message = "An unexpected error occurred";
        }
      })
      .addCase(getOfferIssuerById.pending, (state) => {
        state.offerIssuerDetails.status = "loading";
        state.offerIssuerDetails.isLoading = true;
      })
      .addCase(getOfferIssuerById.fulfilled, (state, action) => {
        state.offerIssuerDetails.status = "idle";
        state.offerIssuerDetails.data = action.payload;
        state.offerIssuerDetails.isLoading = false;
      })
      .addCase(getOfferIssuerById.rejected, (state, action) => {
        state.offerIssuerDetails.status = "failed";
        state.offerIssuerDetails.isLoading = false;
        if (action.payload) {
          state.offerIssuerDetails.error = action.payload;
          state.offerIssuerDetails.message = action.payload;
        } else {
          state.offerIssuerDetails.message = "An unexpected error occurred";
        }
      })
      .addCase(saveOfferIssuer.pending, (state) => {
        state.offerIssuers.status = "loading";
        state.offerIssuers.isLoading = true;
      })
      .addCase(saveOfferIssuer.fulfilled, (state, action) => {
        state.offerIssuers.status = "idle";
        state.offerIssuers.data = action.payload;
        state.offerIssuers.isLoading = false;
      })
      .addCase(saveOfferIssuer.rejected, (state, action) => {
        state.offerIssuers.status = "failed";
        state.offerIssuers.isLoading = false;
        if (action.payload) {
          state.offerIssuers.error = action.payload;
          state.offerIssuers.message = action.payload;
        } else {
          state.offerIssuers.message = "An unexpected error occurred";
        }
      }) 
      .addCase(deleteOfferIssuer.pending, (state) => {
        state.offerIssuerList.status = "loading";
      })
      .addCase(deleteOfferIssuer.fulfilled, (state, action) => {
        state.offerIssuerList.status = "idle";
        state.offerIssuerList.data = action.payload;
        state.offerIssuerList.isLoading = false;
      })
      .addCase(deleteOfferIssuer.rejected, (state, action) => {
        state.offerIssuerList.status = "failed";
        state.offerIssuerList.isLoading = false;
        if (action.payload) {
          state.offerIssuerList.error = action.payload;
          state.offerIssuerList.message = action.payload;
        } else {
          state.offerIssuerList.message = "An unexpected error occurred";
        }
      })
  },
});

export default offerIssuerReducer.reducer; 
